import { Badge, Button, Input, Switch } from '@monksflow/monks-ui';
import { CircleCheck, X } from 'lucide-react';
import clsx from 'clsx';
import { FileTypeIcon } from '@/shared/ui/FileTypeIcon';
import { canUploadAsVersion, isResolved, renameError } from '../lib/resolution';
import type { IntakeItem, ResolutionOption } from '../model/types';
import { useTranslation } from 'react-i18next';

interface UploadFileRowProps {
  item: IntakeItem;
  onUpdateResolutionOption: (id: string, option: ResolutionOption) => void;
  onRename: (id: string, newName: string) => void;
  onRemove: (id: string) => void;
}

export function UploadFileRow({
  item,
  onUpdateResolutionOption,
  onRename,
  onRemove,
}: UploadFileRowProps) {
  const { t } = useTranslation('projects');

  const isConflicted = item.existing != null;
  const isRenaming = isConflicted && item.option === 'rename';
  const error = renameError(item);
  const resolved = isResolved(item);

  const hint = error ? (
    <span className="text-xs text-destructive">{error}</span>
  ) : isConflicted && item.option === 'version' ? (
    <span className="text-xs text-muted-foreground">
      {t('projectFileUploadGuard.uploadFileRow.uploadsAsANewVersion')}
    </span>
  ) : null;

  return (
    <div
      className={clsx(
        'flex items-center gap-3 rounded-lg border border-l-2 bg-card p-3',
        resolved ? 'border-l-primary' : 'border-l-destructive',
      )}
    >
      <div className="shrink-0">
        <FileTypeIcon fileType={item.file.type} />
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {isRenaming ? (
          <Input
            className="h-9"
            aria-label={t('projectFileUploadGuard.uploadFileRow.newNameFor', {
              fileName: item.file.name,
            })}
            aria-invalid={error != null}
            value={item.newName}
            onChange={(e) => onRename(item.id, e.target.value)}
          />
        ) : (
          <span className="truncate text-sm font-medium" title={item.file.name}>
            {item.file.name}
          </span>
        )}
        {hint}
      </div>

      {isConflicted ? (
        <div
          className="flex shrink-0 items-center gap-2 text-sm"
          title={
            canUploadAsVersion(item)
              ? undefined
              : t('projectFileUploadGuard.uploadFileRow.onlyAvailableForAFile')
          }
        >
          <Switch
            checked={item.option === 'version'}
            disabled={!canUploadAsVersion(item)}
            onCheckedChange={(checked) =>
              onUpdateResolutionOption(item.id, checked ? 'version' : 'rename')
            }
            aria-label={t('projectFileUploadGuard.uploadFileRow.uploadAsNewVersion')}
          />
          <span className={clsx(!canUploadAsVersion(item) && 'text-muted-foreground')}>
            {t('projectFileUploadGuard.uploadFileRow.uploadAsNewVersion')}
          </span>
        </div>
      ) : (
        <Badge variant="secondary" className="shrink-0 gap-1">
          <CircleCheck className="size-3.5 text-success" />
          {t('projectFileUploadGuard.uploadFileRow.ready')}
        </Badge>
      )}

      <Button
        type="button"
        variant="ghost"
        size="mini"
        className="size-9 shrink-0 p-0 text-muted-foreground"
        aria-label={t('projectFileUploadGuard.uploadFileRow.removeFile', {
          fileName: item.file.name,
        })}
        onClick={() => onRemove(item.id)}
      >
        <X className="size-4" />
      </Button>
    </div>
  );
}
